import { Link } from "react-router-dom";

import Text from "../../../shared/ui/Typography/Text";
import Spinner from "../../../shared/ui/Loading/Spinner";
import { formatDateLastMessage } from "../../../utils/date";
import { resolveNotificationActivityTimestamp } from "../lib/groupNotificationsByDate";
import { presentNotification } from "../lib/presentationRegistry";

function isSafeInternalActionUrl(url) {
  if (typeof url !== "string") return false;
  if (!url.startsWith("/")) return false;
  if (url.startsWith("//")) return false;
  if (url.includes("://")) return false;
  const lowered = url.toLowerCase();
  if (lowered.startsWith("/javascript:")) return false;
  return true;
}

function NotificationItem({
  notification,
  onMarkRead,
  isMarking = false,
}) {
  if (!notification) return null;

  const { id, sender, is_read, action, dead } = notification;
  const presentation = presentNotification(notification);
  const activityAt = resolveNotificationActivityTimestamp(notification);
  const senderName = sender?.fullname || sender?.handle || "";
  const showAction =
    action &&
    action.kind === "internal" &&
    typeof action.label === "string" &&
    isSafeInternalActionUrl(action.url) &&
    !dead;
  const showMarkRead = !is_read;
  const hasActions = showAction || showMarkRead;

  const handleMarkRead = () => {
    if (isMarking || is_read || !onMarkRead) return;
    onMarkRead(id);
  };

  return (
    <li
      className={`feature-notifications__item${is_read ? "" : " feature-notifications__item--unread"} feature-notifications__item--tone-${presentation.tone}`}
      data-feature="notification-item"
      data-notification-id={String(id)}
      data-notification-type={presentation.type}
      data-notification-domain={presentation.domain}
      data-notification-tone={presentation.tone}
    >
      {!is_read ? (
        <span className="feature-notifications__sr-only">Непрочитане сповіщення</span>
      ) : null}

      <div
        className={`feature-notifications__icon feature-notifications__icon--${presentation.tone}`}
        aria-hidden="true"
      >
        <i className={presentation.iconClass}></i>
      </div>

      <div className="feature-notifications__body">
        <div className="feature-notifications__title-row">
          {!is_read ? (
            <span className="feature-notifications__unread-dot" aria-hidden="true" />
          ) : null}
          <Text
            as="div"
            size="md"
            weight={is_read ? "regular" : "semibold"}
            tone="primary"
            className="feature-notifications__title"
          >
            {presentation.title}
          </Text>
        </div>

        {presentation.body ? (
          <Text
            as="div"
            size="sm"
            tone="secondary"
            leading="normal"
            className="feature-notifications__supporting"
          >
            {presentation.body}
          </Text>
        ) : null}

        <Text as="div" size="xs" tone="tertiary" className="feature-notifications__meta">
          <span className="feature-notifications__domain-label">
            {presentation.domainLabel}
          </span>
          {senderName ? <span> · {senderName}</span> : null}
          <span> · </span>
          <time dateTime={activityAt || undefined}>
            {formatDateLastMessage(activityAt)}
          </time>
        </Text>
      </div>

      {hasActions ? (
        <div className="feature-notifications__actions">
          {showAction ? (
            <Link
              to={action.url}
              className="feature-notifications__action-link action-btn"
            >
              {action.label}
            </Link>
          ) : null}

          {showMarkRead ? (
            <button
              type="button"
              className="feature-notifications__mark-read"
              onClick={handleMarkRead}
              disabled={isMarking}
              data-loading={isMarking ? "true" : undefined}
              aria-label="Позначити прочитаним"
            >
              {isMarking ? (
                <Spinner size="sm" label="Позначаємо як прочитане" />
              ) : (
                <span className="feature-notifications__mark-read-label">
                  Позначити прочитаним
                </span>
              )}
            </button>
          ) : null}
        </div>
      ) : null}
    </li>
  );
}

export default NotificationItem;
